import React, { useState, useEffect, useCallback } from "react";
import {
  UsersIcon,
  ApplicationsIcon,
  MedicalNotesIcon,
  IdCardIcon,
  PayrollIcon,
  ArrowRightIcon,
  CloseIcon,
} from "../../../Icons";
import { request } from "../../../utils/api";
import { getSessionTenantId } from "../../../utils/tenantContext";
import { StatisticsSkeleton } from "../../../components/Skeletons";
import Table from "../../../components/UI/Table";
import { CELL_STRONG } from "../../../ui/pageChrome";
import {
  REPORT_THEME,
  REPORT_BTN,
  REPORT_MODAL,
  REPORT_ROLE_CHIP,
} from "../reportTheme";

const Card = ({ title, count, icon: Icon, subtext, onClick }) => {
  const interactive = typeof onClick === "function";
  const hasNodeSubtext = subtext != null && typeof subtext !== "string";

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      className={`bg-white rounded-xl p-5 border shadow-sm transition-all duration-200 relative overflow-hidden ${REPORT_THEME.section.wrap} ${
        interactive
          ? "cursor-pointer hover:shadow-md hover:border-indigo-300 group"
          : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="relative z-10 min-w-0 w-full">
          <p className="text-slate-500 text-sm font-medium mb-1 truncate">
            {title}
          </p>
          <h3 className="text-3xl font-bold text-slate-900 tabular-nums">
            {count}
          </h3>

          <div
            className={`mt-1 relative ${hasNodeSubtext ? "min-h-[1.25rem]" : "h-5"}`}
          >
            <div
              className={`${hasNodeSubtext ? "" : "absolute top-0 left-0 w-full"} transition-all duration-200 ${
                interactive
                  ? "group-hover:opacity-0 group-hover:-translate-y-1"
                  : ""
              }`}
            >
              {subtext ? (
                typeof subtext === "string" ? (
                  <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        count > 0 ? "bg-amber-500" : "bg-emerald-500"
                      }`}
                    />
                    {subtext}
                  </span>
                ) : (
                  subtext
                )
              ) : (
                <span className="text-xs text-slate-400 block font-medium">
                  —
                </span>
              )}
            </div>

            {interactive ? (
              <div className="absolute top-0 left-0 w-full opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 flex items-center gap-1 text-xs font-semibold text-indigo-700">
                View Record <ArrowRightIcon size={14} />
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex-shrink-0 p-3 rounded-lg bg-indigo-50 text-indigo-600 group-hover:scale-105 transition-transform duration-200">
          {Icon ? <Icon size={22} /> : null}
        </div>
      </div>
    </div>
  );
};

const OverviewCards = ({ data, loading: loadingOverview }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [modalTitle, setModalTitle] = useState("");
  const [reportType, setReportType] = useState("");

  const fetchReportDetails = useCallback(async (type, pageNum) => {
    setLoading(true);
    try {
      const tenantId = getSessionTenantId();
      if (!tenantId) return;

      const endpointMap = {
        application: "/reports/application-forms/missing",
        medical: "/reports/medical-forms/missing",
        credentials: "/reports/credentials-forms/missing",
        tax: "/reports/tax-and-payroll/missing",
      };

      const response = await request(
        `${endpointMap[type]}?tenantId=${tenantId}&page=${pageNum}&limit=10`,
      );
      if (response.status === 1) {
        setReportData(response.result.items || []);
        setTotalPages(response.result.totalPages || 0);
        setTotalItems(
          response.result.total ||
            response.result.totalItems ||
            response.result.items?.length ||
            0,
        );
      }
    } catch (err) {
      console.error("Error fetching report details:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen && reportType) {
      fetchReportDetails(reportType, page);
    }
  }, [isModalOpen, reportType, page, fetchReportDetails]);

  const handleCardClick = (type, title) => {
    setReportType(type);
    setModalTitle(title);
    setPage(1);
    setTotalItems(0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setReportData([]);
    setTotalItems(0);
  };

  if (loadingOverview) {
    return (
      <div>
        <StatisticsSkeleton />
      </div>
    );
  }

  if (!data || !data.statistics) return null;

  const {
    totalEmployees,
    activeEmployees,
    inactiveEmployees,
    applicationForms,
    medicalForms,
    credentialsForUpload,
    taxForms,
  } = data.statistics;

  const activeCount = activeEmployees ?? totalEmployees ?? 0;
  const inactiveCount = inactiveEmployees ?? 0;
  const workforceTotal = totalEmployees ?? activeCount + inactiveCount;

  const columns = [
    {
      header: "Employee Name",
      accessor: "userName",
      className: "min-w-0 pl-3 pr-2 align-middle",
      render: (row) => (
        <span className={CELL_STRONG}>{row.userName || "—"}</span>
      ),
    },
    {
      header: "Role",
      accessor: "role",
      className: "whitespace-nowrap px-2 align-middle",
      render: (row) => (
        <span
          className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${REPORT_ROLE_CHIP}`}
        >
          {row.role || "—"}
        </span>
      ),
    },
    {
      header: "Form Name",
      accessor: "form",
      className: "min-w-0 px-2 align-middle",
      render: (row) => (
        <span className="text-[13px] text-slate-800">{row.form || "—"}</span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      className: "whitespace-nowrap px-2 align-middle",
      render: (row) => (
        <span className="inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide bg-red-50 text-red-700 border-red-200">
          {row.status || "Missing"}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <Card
          title="Total Workforce"
          count={workforceTotal}
          icon={UsersIcon}
          subtext={
            <div className="text-xs text-slate-500 font-medium flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-emerald-700 font-semibold">
                  {activeCount}
                </span>{" "}
                Active
              </span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                <span className="text-slate-700 font-semibold">
                  {inactiveCount}
                </span>{" "}
                Inactive
              </span>
            </div>
          }
        />
        <Card
          title="Applications Missing"
          count={applicationForms?.missingCount || 0}
          icon={ApplicationsIcon}
          subtext={`${applicationForms?.filledCount || 0} Completed`}
          onClick={() =>
            handleCardClick("application", "Application Forms Missing")
          }
        />
        <Card
          title="Medical Health"
          count={medicalForms?.missingCount || 0}
          icon={MedicalNotesIcon}
          subtext={`${medicalForms?.filledCount || 0} Records`}
          onClick={() => handleCardClick("medical", "Medical Forms Missing")}
        />
        <Card
          title="Staff Credentials"
          count={credentialsForUpload?.missingCount || 0}
          icon={IdCardIcon}
          subtext={`${credentialsForUpload?.uploadedCount || 0} Active`}
          onClick={() => handleCardClick("credentials", "Credentials Missing")}
        />
        <Card
          title="Tax Compliance"
          count={taxForms?.missingCount || 0}
          icon={PayrollIcon}
          subtext={`${taxForms?.missingCount || 0} Forms Missing`}
          onClick={() => handleCardClick("tax", "Tax Forms Missing")}
        />
      </div>

      {isModalOpen ? (
        <div
          className={REPORT_MODAL.backdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="missing-report-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div className={`${REPORT_MODAL.panel} h-[700px]`}>
            <div className={REPORT_MODAL.head}>
              <div className="min-w-0">
                <h2 id="missing-report-title" className={REPORT_MODAL.title}>
                  {modalTitle}
                </h2>
                <p className={REPORT_MODAL.subtitle}>
                  Missing documentation for this category
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className={REPORT_BTN.modalClose}
                title="Close"
                aria-label="Close"
              >
                <CloseIcon size={14} />
              </button>
            </div>

            <div className={`${REPORT_MODAL.body} p-4`}>
              <div
                className={`bg-white rounded-xl border overflow-hidden ${REPORT_THEME.section.wrap}`}
              >
                <Table
                  columns={columns}
                  data={reportData}
                  loading={loading}
                  showSrNo={false}
                  density="compact"
                  embedded
                  headTone={REPORT_THEME.tableHead}
                  pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalItems: totalItems,
                    onPageChange: setPage,
                    itemsPerPage: 10,
                  }}
                  noDataMessage="No missing records found."
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default OverviewCards;
